/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from '@ag-grid-community/core';
import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import { set } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { getTrialAttributes } from '@features/budget-attributes/services/budget-attributes.service';

import { BudgetQuery } from '@models/budget/budget.query';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { ApiService } from '@shared/services/api.service';
import {
  Currency,
  EntityType,
  EventType,
  GqlService,
  listQuarterCloseExpensesQuery,
  listUserNamesWithEmailQuery,
  User,
  WorkflowStep,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { AuthService, injectPermissions } from '@shared/store/auth/auth.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { CurrencyToggle, Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { QuarterCloseDialogComponent } from '../quarter-close-dialog/quarter-close-dialog.component';
import { quarterCloseGridConfig } from './grid-config/qc-grid-config';
import { PeriodCloseComponent } from '../../../period-close.component';
import { QCGridData } from '../../../period-close.model';
import { QcReviewNotesModalComponent } from '../qc-review-notes-modal/qc-review-notes-modal.component';
import {
  QcReviewNotesModalInput,
  QcReviewNotesModalOutput,
} from '../qc-review-notes-modal/qc-review-notes-modal.model';
import { QuarterCloseProcessingConfirmationComponent } from '../quarter-close-processing-confirmation.component';
import { QuarterCloseService } from '../quarter-close.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular, StickyGridDirective],
  selector: 'aux-qc-table',
  template: `
    @if (!loading()) {
      <ag-grid-angular
        class="new-flat-theme w-full tabular-nums"
        domLayout="autoHeight"
        [rowData]="rowData()"
        [gridOptions]="gridOptions()"
        (gridReady)="onGridReady($event)"
        (firstDataRendered)="onFirstDataRendered()"
        (filterChanged)="onFilterChanged()"
        auxStickyGrid
        [stickyOffset]="stickyServiceOffset()"
      />
    }
  `,
})
export class QcTableComponent {
  apiService = inject(ApiService);
  authService = inject(AuthService);
  budgetQuery = inject(BudgetQuery);
  eventTrackerService = inject(EventTrackerService);
  globalEventService = inject(EventService);
  globalStore = inject(GlobalStore);
  invoiceService = inject(InvoiceService);
  mainQuery = inject(MainQuery);
  organizationQuery = inject(OrganizationQuery);
  overlayService = inject(OverlayService);
  periodCloseComponent = inject(PeriodCloseComponent);
  periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  quarterCloseService = inject(QuarterCloseService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  workflowQuery = inject(WorkflowQuery);

  private gqlService = inject(GqlService);

  flags = injectFlags({
    isAPColumnVisible: 'advanced_payments_ltd_columns',
    isContractAmountColumnVisible: 'close_budget_total',
    isMonthTabAvailable: 'tab_in_month_adjustments',
    isNPColumnVisible: 'net_position_columns',
    isUnpaidColumnsVisible: 'unpaid_invoices_ltd_column',
  });
  permissions = injectPermissions({
    canCloseMonth: 'PERMISSION_APPROVE_IN_MONTH',
    canModifyAdjustments: 'PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS',
  });

  gridAPI = signal<GridApi<QCGridData> | null>(null);
  gridOptionsLoading = signal(false);

  isCloseMonthAvailable = input.required<boolean>();
  rowData = input.required<QCGridData[]>();

  openReviewNotesModal = async (params: CellClickedEvent): Promise<void> => {
    const resp = this.overlayService.openPopup<
      QcReviewNotesModalInput,
      QcReviewNotesModalOutput,
      QcReviewNotesModalComponent
    >({
      content: QcReviewNotesModalComponent,
      data: {
        cost_category: params.data.cost_category,
        current_quarter: this.periodCloseComponent.selectedQuarterSignal().replace('-', ' '),
        group_value: params.data.group_value,
        isCurrentQuarterSelected: this.periodCloseComponent.isCurrentQuarterSelected(),
        selectedQuarterMonths: this.selectedQuarterMonths(),
        vendor_id: params.data.vendor_id,
      },
      settings: {
        header: `${this.periodCloseComponent.selectedQuarterSignal().replace('-', ' ')} - Review and Flux Notes`,
        panelClasses: ['w-[700px]'],
        primaryButton: {
          action: (instance) => instance?.onSaveClick(),
          disabled: (instance) => !!instance?.isSaveBtnDisabled(),
          hidden: !this.periodCloseComponent.isCurrentQuarterSelected(),
          loading: (instance) => !!instance?.loading(),
          pendoTag: 'close-summary-review-notes-save-button',
          tooltip: (instance) => (instance ? instance?.saveButtonTooltip() : ''),
        },
        subHeader: this.getSubHeader(params),
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      set(params.data, 'number_of_notes_for_selected_quarter', event.data.notesCount);
      this.gridAPI()?.refreshCells({ rowNodes: [params.node] });
    }
  };

  gridOptions = computed<GridOptions<QCGridData>>(() => {
    const attributeConfig = untracked(() => ({
      attributes: getTrialAttributes(
        this.globalStore.attributes(),
        this.globalStore.primaryAttribute().attribute_name,
        this.globalStore.secondaryAttribute().attribute_name
      ),
      primaryAttribute: this.globalStore.primaryAttribute(),
      secondaryAttribute: this.globalStore.secondaryAttribute(),
    }));
    const isCurrentQuarterSelected = this.periodCloseComponent.isCurrentQuarterSelected();
    const organizations = this.organizationQuery.allVendors();
    const rowData = this.rowData();
    const flags = this.flags();
    const permissions = this.permissions();
    const selectedQuarterMonths = this.periodCloseComponent.selectedQuarterMonths();
    const selectedQuarter = this.periodCloseComponent.selectedQuarterSignal() || '';
    const openMonth = this.periodCloseComponent.currentMonthSignal();
    const isWorkflowAvailable = this.workflowQuery.isWorkflowAvailable();
    const adjustmentWorkflow = this.workflowQuery.getWorkflowByStepType(
      WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
    )();
    const isCloseMonthAvailable = this.isCloseMonthAvailable();
    const isCloseMonthsProcessing = !!this.periodCloseComponent.isCloseMonthsProcessing();

    return untracked(() => {
      return {
        ...quarterCloseGridConfig(
          {
            adjustmentWorkflow,
            attributeConfig: {
              ...attributeConfig,
              gridData: rowData,
            },
            flags,
            gridData: rowData,
            isCloseMonthAvailable,
            isCloseMonthsProcessing,
            isCurrentQuarterSelected,
            isWorkflowAvailable,
            onInvoiceCellClicked: (params) => {
              this.navigateByInvoices(params);
            },
            onMonthCloseClicked: () => {
              void this.onInMonthClose();
            },
            onMonthlyWpEstimateCellClicked: (params: ICellRendererParams<QCGridData>) => {
              this.periodCloseComponent.onSelectionChanged(params);
            },
            openMonth,
            organizations,
            permissions,
            selectedQuarter,
            selectedQuarterMonths,
          },
          this.quarterCloseService,
          this.openReviewNotesModal
        ),
        pinnedBottomRowData: this.calculateBottomRowData(rowData),
      };
    });
  });

  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');

  loading = computed(() => {
    return (
      this.gridOptionsLoading() || this.trialAttributesLoading() || this.organizationQuery.loading()
    );
  });

  stickyServiceOffset = input.required<number>();

  _syncGridOptions = effect(() => {
    // any changes to the grid options will trigger a re-render
    this.gridOptions();

    // don't track the gridAPI
    const gridAPI = untracked(() => this.gridAPI());

    if (!gridAPI) {
      return;
    }

    this.gridOptionsLoading.set(true);
    setTimeout(() => {
      this.gridOptionsLoading.set(false);
    }, 0);
  });
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();

  async actionGridByRouteParams() {
    const { jeSelectedMonth, jeVendorId: _ } = await firstValueFrom(this.route.queryParams);
    const gridAPI = this.gridAPI()!;
    const months = this.periodCloseComponent.selectedQuarterMonths();

    const index = months.findIndex((x) => x === jeSelectedMonth);

    if (index === -1) {
      return false;
    }

    // dont need it anymore
    // if (jeVendorId) {
    //   gridAPI.forEachNode((node) => {
    //     if (node.field === 'vendor_id' && node.key === jeVendorId) {
    //       gridAPI.setRowNodeExpanded(node, true);
    //     }
    //   });
    // }

    setTimeout(() => {
      gridAPI.ensureColumnVisible(`qmonth${index + 1}_accrual_ltd`, 'start');
    }, 0);

    this.clearRouterQueryParams();
  }

  // eslint-disable-next-line max-lines-per-function
  calculateBottomRowData(gridData: QCGridData[]) {
    const flags = this.flags();
    const gridAPI = this.gridAPI();

    const isPrimaryCurrency = this.periodCloseComponent.currencyToggle() === CurrencyToggle.PRIMARY;

    const filteredData: QCGridData[] = [];
    const currencySet = new Set<Currency>();

    gridAPI?.forEachNodeAfterFilter((node) => {
      filteredData.push(node.data!);
    });

    const data = gridAPI ? filteredData : gridData;

    data.forEach((row) => {
      currencySet.add(row.currency);
    });

    if (!isPrimaryCurrency && currencySet.size !== 1) {
      return [];
    }

    const obj: Record<string, number> = {
      monthly_accrual_prepaid_difference: 0,
      monthly_expense_variance: 0,
      monthly_invoices_variance: 0,
      previous_month_accrual_ltd: 0,
      previous_month_invoices_received: 0,
      previous_month_prepaid_ltd: 0,
      previous_month_wp_estimate: 0,
      previous_quarter_total_accrual: 0,
      previous_quarter_total_invoiced: 0,
      previous_quarter_total_prepaid: 0,
      previous_quarter_total_wp: 0,
      qend_accrual_ltd: 0,
      qend_invoiced_ltd: 0,
      qend_monthly_invoices_received: 0,
      qend_net_change_in_accruals: 0,
      qend_prepaid_ltd: 0,
      qend_quarterly_wp_estimate: 0,
      qend_unpaid_invoices_ltd: 0,
      qend_wp_ltd: 0,
      qmonth1_total_expense_quote: 0,
      qstart_accrual_ltd: 0,
      qstart_invoiced_ltd: 0,
      qstart_prepaid_ltd: 0,
      qstart_wp_ltd: 0,
      quarterly_accrual_prepaid_difference: 0,
      quarterly_expense_variance: 0,
      quarterly_invoices_variance: 0,
    };
    const monthColumns = [
      '_accrual_debit',
      '_accrual_credit',
      '_prepaid_debit',
      '_prepaid_credit',
      '_monthly_wp_estimate',
      '_monthly_invoices_received',
      '_net_change_in_accruals',
      '_wp_ltd',
      '_invoiced_ltd',
      '_accrual_ltd',
      '_prepaid_ltd',
      '_unpaid_invoices_ltd',
    ];

    if (flags.isAPColumnVisible) {
      monthColumns.push('_advanced_payments_ltd');
    }

    if (flags.isNPColumnVisible) {
      monthColumns.push('_net_position');
    }

    monthColumns.forEach((field) => {
      obj[`qmonth1${field}`] = 0;
      obj[`qmonth2${field}`] = 0;
      obj[`qmonth3${field}`] = 0;
    });

    const columnsToCalc = Object.keys(obj) as (keyof Omit<
      listQuarterCloseExpensesQuery,
      '__typename' | 'attributes' | 'cost_category' | 'group_value' | 'vendor_id' | 'vendor_name'
    >)[];

    for (const row of data) {
      for (const column of columnsToCalc) {
        obj[column] += row[column] || 0;
      }
    }

    const fluxPercentageTotals = {
      monthly_accrual_prepaid_difference_percentage: 0,
      monthly_expense_variance_percentage: 0,
      monthly_invoices_variance_percentage: 0,
      quarterly_accrual_prepaid_difference_percentage: 0,
      quarterly_expense_variance_percentage: 0,
      quarterly_invoices_variance_percentage: 0,
    };

    if (this.quarterCloseService.isCurrentQuarterSelected() && data.length > 0) {
      fluxPercentageTotals.monthly_accrual_prepaid_difference_percentage =
        data[0].total_monthly_accrual_prepaid_difference_percentage || 0;
      fluxPercentageTotals.monthly_expense_variance_percentage =
        data[0].total_monthly_expense_variance_percentage || 0;
      fluxPercentageTotals.monthly_invoices_variance_percentage =
        data[0].total_monthly_invoices_variance_percentage || 0;
      fluxPercentageTotals.quarterly_accrual_prepaid_difference_percentage =
        data[0].total_quarterly_accrual_prepaid_difference_percentage || 0;
      fluxPercentageTotals.quarterly_expense_variance_percentage =
        data[0].total_quarterly_expense_variance_percentage || 0;
      fluxPercentageTotals.quarterly_invoices_variance_percentage =
        data[0].total_quarterly_invoices_variance_percentage || 0;
    }

    const currency = isPrimaryCurrency ? Currency.USD : currencySet.values().next().value;

    return [
      {
        ...obj,
        activity_name: 'Total',
        currency,
        ...fluxPercentageTotals,
      },
    ];
  }

  // todo(refactor) The following methods need to be refactored
  clearRouterQueryParams() {
    void this.router.navigate([], {
      queryParams: {
        jeSelectedMonth: null,
        jeVendorId: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  constructor() {
    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((users) => {
        users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });
  }

  getSubHeader(params: CellClickedEvent): string {
    return `${params.data.vendor_name} / ${Object.values(JSON.parse(JSON.parse(params.data.group_value))).join(' / ')}`;
  }

  // eslint-disable-next-line max-lines-per-function, complexity
  navigateByInvoices(event: CellClickedEvent) {
    const navigateParams: { acc_months: Option[]; vendor_ids: string[] } = {
      acc_months: [],
      vendor_ids: [],
    };

    const columnId = event.column.getColId();
    const isTotalRow = event.rowPinned === 'bottom';

    const isInvoiceUnpaid = columnId.includes('unpaid');
    const inQStart = columnId.includes('start');
    const inQSummary = columnId.includes('end') || columnId.includes('total');
    const inQMonth = !inQStart && !inQSummary;

    const all_options = this.invoiceService.accrualPeriodOptions;
    const [qtr, year] = this.periodCloseComponent.selectedQuarter.value?.split('-') || [];

    if (!all_options || !qtr || !year) {
      return;
    }

    const selectedQtrDate = new Date(+year, +qtr.slice(1) * 3 - 3, 1);

    const vendorIds = this.organizationQuery.allVendors().map((v) => v.id);

    navigateParams.vendor_ids = isTotalRow ? vendorIds : [];

    if (inQMonth) {
      const isInvoiceReceived = columnId.includes('received');
      const isInvoiceLtd = columnId.includes('ltd');
      const headerName = event.column.getOriginalParent()?.getColGroupDef()?.headerName;

      if (headerName) {
        const headerDayjs = dayjs(headerName);

        if (!headerDayjs.isValid()) {
          console.error('Was not able to parse header name', headerName);

          return;
        }

        const month = headerDayjs.month();
        const year = headerDayjs.year().toString();

        if (isInvoiceReceived) {
          const acc_option = getAccrualPeriodOptionsForMonths(month, year, all_options).acc_option;

          navigateParams.acc_months = [];
          navigateParams.acc_months.push(acc_option);
        }

        if ((isInvoiceLtd && !isInvoiceReceived) || isInvoiceUnpaid) {
          navigateParams.acc_months = getAccrualPeriodOptionsForMonths(
            month,
            year,
            all_options
          ).before_and_current_options;
        }
      }
    } else if (inQStart) {
      navigateParams.acc_months = getAccrualPeriodOptionsForQStart(
        all_options,
        selectedQtrDate
      ).before_acc_options;
    } else if (inQSummary) {
      const isInvoiceReceived = columnId.includes('received');
      const isInvoiceLtd = columnId.includes('ltd');
      const isInvoiceUnpaid = columnId.includes('unpaid');

      if (isInvoiceReceived) {
        navigateParams.acc_months = getAccrualPeriodOptionsForQSummary(
          all_options,
          selectedQtrDate
        ).quarter_acc_options;
      }

      if ((isInvoiceLtd && !isInvoiceReceived) || isInvoiceUnpaid) {
        navigateParams.acc_months = getAccrualPeriodOptionsForQSummary(
          all_options,
          selectedQtrDate
        ).before_and_quarter_acc_options;
      }
    }

    const acc_mont_values: string[] = [];

    navigateParams.acc_months.forEach((month) => {
      acc_mont_values.push(dayjs(month.value).format('YYYY-MM-DD'));
    });

    this.invoiceService.setAccrualPeriodsAndVendorFilter(
      acc_mont_values,
      navigateParams.vendor_ids
    );

    if (isInvoiceUnpaid) {
      this.invoiceService.setShowUnpaidInvoices(true);
    }

    void this.router.navigateByUrl(
      `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`
    );
  }

  onFilterChanged() {
    this.gridAPI()?.setGridOption(
      'pinnedBottomRowData',
      this.calculateBottomRowData(this.rowData())
    );
  }

  async onFirstDataRendered() {
    const result = await this.actionGridByRouteParams();

    if (!result) {
      const openMonth = this.periodCloseComponent.currentMonthSignal();
      const selectedQuarterMonths = this.periodCloseComponent.selectedQuarterMonths();
      const index = selectedQuarterMonths.findIndex((x) => x === openMonth);

      if (index === -1) {
        return;
      }

      const fluxAnalysisAvailableAndOn =
        localStorage.getItem(LocalStorageKey.FLUX_ANALYSIS_MODE) === 'true' &&
        this.quarterCloseService.isFluxAnalysisAvailable();

      this.gridAPI()!.ensureColumnVisible(
        fluxAnalysisAvailableAndOn
          ? 'previous_month_wp_estimate'
          : `qmonth${index + 1}_monthly_wp_estimate`,
        'start'
      );
    }
  }

  onGridReady(event: GridReadyEvent<QCGridData>) {
    this.gridAPI.set(event.api);

    this.gridAPI()?.setGridOption(
      'pinnedBottomRowData',
      this.calculateBottomRowData(this.rowData())
    );
  }

  // eslint-disable-next-line max-lines-per-function
  async onInMonthClose() {
    this.gridAPI()?.setFilterModel({});
    const info = this.budgetQuery.getValue().budget_info;

    const first_vendor_current_month = info[0]?.current_month;

    if (!first_vendor_current_month) {
      return;
    }

    const formatted_current_month = formatDate(first_vendor_current_month, 'MMMM y', 'en-US');

    const resp = await firstValueFrom(
      this.overlayService.openPopup<
        unknown,
        undefined | { note: string; sendToNetsuite?: boolean },
        QuarterCloseDialogComponent
      >({
        content: QuarterCloseDialogComponent,
        data: {
          formatted_current_month,
        },
        settings: {
          header: `Close ${formatted_current_month}`,
          primaryButton: {
            action: (instance) => {
              instance?.close(true);
            },
            label: `Close ${formatted_current_month}`,
            pendoTag: 'close-month',
            variant: 'success',
          },
        },
      }).afterClosed$
    );

    if (!resp.data?.note) {
      return;
    }

    await firstValueFrom(
      this.overlayService.openPopup({
        content: QuarterCloseProcessingConfirmationComponent,
        settings: {
          header: 'The Close Process has begun',
          primaryButton: {
            label: 'OK',
          },
          secondaryButton: {
            hidden: true,
          },
        },
      }).afterClosed$
    );

    const ref = this.overlayService.loading();

    const { current_month } = info[0];

    const result = await firstValueFrom(
      this.globalEventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          event_dates: [current_month],
          note: resp.data?.note,
          sendToNetsuite: resp.data?.sendToNetsuite,
        }),
        type: EventType.CLOSE_TRIAL_MONTH,
      })
    );

    ref.close();

    if (result.success) {
      void this.eventTrackerService.trackEvent(result.data!.id);
      this.periodCloseComponent.monthClosed.set(true);
    } else {
      const allErrors: string[] = [];

      allErrors.push(...result.errors);
      this.overlayService.error(allErrors);
    }
  }

  selectedQuarterMonths(): string[] {
    if (!this.periodCloseComponent.isCurrentQuarterSelected()) {
      return this.periodCloseComponent.selectedQuarterMonths();
    }

    return this.periodCloseComponent.selectedQuarterMonths().filter((month) => {
      return !dayjs(month).isAfter(dayjs(this.periodCloseService.currentOpenMonth));
    });
  }
}

const getAccrualPeriodOptionsForQStart = (all_options: Option[], date: Date) => {
  const before_acc_options = all_options.filter((option) =>
    dayjs(`${option.value}`).isBefore(date)
  );

  return { before_acc_options };
};

const getAccrualPeriodOptionsForQSummary = (all_options: Option[], date: Date) => {
  const quarter_acc_options = all_options.filter(
    (option) =>
      dayjs(`${option.value}`).quarter() === dayjs(date).quarter() &&
      dayjs(`${option.value}`).year() === dayjs(date).year()
  );
  const before_and_quarter_acc_options = all_options.filter(
    (option) =>
      (dayjs(`${option.value}`).quarter() === dayjs(date).quarter() &&
        dayjs(`${option.value}`).year() === dayjs(date).year()) ||
      dayjs(`${option.value}`).isBefore(date)
  );

  return { before_and_quarter_acc_options, quarter_acc_options };
};

const getAccrualPeriodOptionsForMonths = (
  month_number: number,
  year: string,
  all_options: Option[]
) => {
  const acc_option: Option = { label: '', value: '' };

  const longMonthNames = Utils.MONTH_NAMES;
  const longMonthName = longMonthNames[month_number];

  acc_option.label = `${longMonthName} ${year}`;
  acc_option.value = `${year}-${month_number + 1}`;

  const date = new Date(+year, month_number, 1);

  const before_and_current_options = all_options.filter((option) =>
    dayjs(`${option.value}`).isSameOrBefore(date)
  );

  return { acc_option, before_and_current_options };
};
